// WHAT: Log Domain Entity Definitions
// WHY:  Defines available log domains with configuration (labels, icons, colors) for categorization
// DOMAIN: Logging
// DEPENDENCIES: None
// RISKS:
//   - NOTE: 'custom' domain is defined but not in LOG_DOMAINS array (may cause inconsistency)
//   - NOTE: Color values are hardcoded hex strings (not from design tokens)
//   - NOTE: getLogDomainConfig warns on console but doesn't return fallback config

//! Log Domain Definitions

export type LogDomain =
  | "admin"
  | "api"
  | "auth"
  | "core"
  | "custom"
  | "database"
  | "health"
  | "investments"
  | "llm"
  | "productivity"
  | "settings"
  | "smarthome"
  | "system"
  | "ui";

export const LOG_DOMAINS: LogDomain[] = [
  "api",
  "ui",
  "database",
  "auth",
  "health",
  "productivity",
  "admin",
  "core",
  "smarthome",
  "settings",
  "system",
  "llm",
  "investments",
];

export const LOG_DOMAIN_CONFIG = {
  admin: {
    color: "#6b7280", // gray-500
    icon: "⚙️",
    label: "Admin",
  },
  api: {
    color: "#8b5cf6", // violet-500
    icon: "🌐",
    label: "API",
  },
  auth: {
    color: "#f59e0b", // amber-500
    icon: "🔐",
    label: "Auth",
  },
  core: {
    color: "#84cc16", // lime-500
    icon: "🔧",
    label: "Core",
  },
  custom: {
    color: "#8b5cf6", // violet-500
    icon: "📦",
    label: "Custom",
  },
  database: {
    color: "#10b981", // emerald-500
    icon: "🗄️",
    label: "Database",
  },
  health: {
    color: "#ef4444", // red-500
    icon: "❤️",
    label: "Health",
  },
  investments: {
    color: "#10b981", // emerald-500
    icon: "📈",
    label: "Investments",
  },
  llm: {
    color: "#8b5cf6", // violet-500
    icon: "🤖",
    label: "LLM",
  },
  productivity: {
    color: "#3b82f6", // blue-500
    icon: "⚡",
    label: "Productivity",
  },
  settings: {
    color: "#6366f1", // indigo-500
    icon: "⚙️",
    label: "Settings",
  },
  smarthome: {
    color: "#f97316", // orange-500
    icon: "🏠",
    label: "SmartHome",
  },
  system: {
    color: "#374151", // gray-700
    icon: "🖥️",
    label: "System",
  },
  ui: {
    color: "#06b6d4", // cyan-500
    icon: "🖥️",
    label: "UI",
  },
} as const;

/**
 * WHAT:
 *   Retrieves configuration for a specific log domain.
 *
 * WHY:
 *   Provides access to domain metadata (label, icon, color) for UI display.
 *
 * INPUTS:
 *   - domain: LogDomain — The log domain to get configuration for
 *
 * OUTPUT:
 *   - object | undefined — Domain configuration object or undefined if not found
 *
 * BEHAVIOR:
 *   - Looks up domain in LOG_DOMAIN_CONFIG
 *   - Logs warning to console if domain not found
 *   - Returns undefined if domain doesn't exist (caller must handle)
 *
 * RISKS:
 *   - NOTE: Returns undefined for unknown domains (no fallback config)
 *   - NOTE: Warning is logged but may not be visible in production
 *   - NOTE: 'custom' domain exists in type but not in LOG_DOMAINS array
 *
 * @param domain - The log domain to get configuration for
 *
 * @returns Domain configuration object or fallback config for unknown domains
 *
 */
export const getLogDomainConfig = (domain: LogDomain) => {
  // NOTE: TypeScript ensures domain is always valid at compile time
  // At runtime, we return the config directly (no fallback needed)
  return LOG_DOMAIN_CONFIG[domain];
};
